// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Project Complexity Defaults Panel
// apps/web/src/features/project/ComplexityDefaultsPanel.tsx
//
// Stamps the six dimensional defaults (ladders 1-5, integration points 0-50)
// and expertise-domain list onto a project. Includes a dry-run preview before
// applying defaults to existing work items with NULL dimensions. See #3 in
// the complexity-profile plan; scales per ruling W-1.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../../components/engine/tokens';
import { GlassPanel, SectionLabel } from '../../components/engine/EnginePageShell';
import {
  useComplexityDefaults,
  useComplexityDefaultsMutations,
  type ComplexityDefaults,
  type ApplyDefaultsResult,
} from '../../lib/api/projects';

// Ladder dimensions are canonically 1–5 (ruling W-1: the classifier clamps
// 1–5, the server ClampScore is 1–5, the composite formula ×2 maps to 1–10).
// Integration Points is a 0–50 COUNT, not a ladder.
const DIMENSIONS = [
  { key: 'defaultArchitecturalDepth',              label: 'Architectural Depth',     max: 5,  hint: 'Layers, abstractions, coupling. CRUD = 2, distributed event-sourced = 5' },
  { key: 'defaultIntegrationPoints',               label: 'Integration Points',      max: 50, hint: 'External systems / APIs / SDKs touched (a count, not a 1–5 ladder)' },
  { key: 'defaultDomainExpertiseRequired',         label: 'Domain Expertise',        max: 5,  hint: 'Business-domain knowledge depth needed' },
  { key: 'defaultInfrastructureExpertiseRequired', label: 'Infrastructure',          max: 5,  hint: 'Cloud / k8s / networking depth' },
  { key: 'defaultSecurityExpertiseRequired',       label: 'Security',                max: 5,  hint: 'Cryptography, auth, compliance, threat modeling' },
  { key: 'defaultNoveltyScore',                    label: 'Novelty',                 max: 5,  hint: '1 = boilerplate, 5 = novel research' },
] as const satisfies ReadonlyArray<{ key: keyof ComplexityDefaults; label: string; max: number; hint: string }>;

export function ComplexityDefaultsPanel({ projectId }: { projectId: number }) {
  const { data, refetch } = useComplexityDefaults(projectId);
  const { save, applyToOrphans, loading } = useComplexityDefaultsMutations(projectId);

  const [form, setForm] = useState<ComplexityDefaults>({
    defaultArchitecturalDepth: null,
    defaultIntegrationPoints: null,
    defaultDomainExpertiseRequired: null,
    defaultInfrastructureExpertiseRequired: null,
    defaultSecurityExpertiseRequired: null,
    defaultNoveltyScore: null,
    defaultExpertiseDomainsJson: null,
  });
  const [domainsText, setDomainsText] = useState('');
  const [preview, setPreview] = useState<ApplyDefaultsResult | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setForm(data);
    if (data.defaultExpertiseDomainsJson) {
      try {
        const arr = JSON.parse(data.defaultExpertiseDomainsJson);
        if (Array.isArray(arr)) setDomainsText(arr.join(', '));
      } catch { /* ignore malformed json */ }
    }
  }, [data]);

  const setField = (key: keyof ComplexityDefaults, value: number | null) =>
    setForm(f => ({ ...f, [key]: value }));

  const onSave = async () => {
    const domains = domainsText
      .split(',').map(s => s.trim()).filter(Boolean);
    const payload: ComplexityDefaults = {
      ...form,
      defaultExpertiseDomainsJson: domains.length > 0 ? JSON.stringify(domains) : null,
    };
    const ok = await save(payload);
    if (ok) {
      setStatusMsg('Saved. Use Apply to fill existing work items.');
      refetch();
    }
  };

  const onPreview = async () => {
    setStatusMsg(null);
    const res = await applyToOrphans(true);
    if (res) setPreview(res);
  };

  const onApply = async () => {
    const res = await applyToOrphans(false);
    if (res) {
      setStatusMsg(`Applied: ${res.workItemsTouched} work items, ${res.totalFieldsFilled} fields filled.`);
      setPreview(null);
    }
  };

  return (
    <GlassPanel accent={C.violet} delay={0.3} style={{ marginBottom: 20, padding: 18 }}>
      <SectionLabel color={C.violet}>Complexity Defaults</SectionLabel>
      <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
        Default dimensional scores (ladders 1–5, integration points 0–50) stamped onto new
        work items when the import/classify pipeline doesn't supply them. Fills the investor
        radar's complexity profile and gives the AI report something concrete to anchor
        narratives against. User-edited scores and classifier-derived scores are never
        overwritten.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        {DIMENSIONS.map(dim => (
          <DimensionSlider
            key={dim.key}
            label={dim.label}
            hint={dim.hint}
            max={dim.max}
            value={form[dim.key] as number | null}
            onChange={v => setField(dim.key, v)}
          />
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 6 }}>
          EXPERTISE DOMAINS (comma-separated)
        </div>
        <input
          type="text"
          value={domainsText}
          onChange={e => setDomainsText(e.target.value)}
          placeholder="nbfi-lending, multi-tenant-saas, compliance, azure, ml-classification"
          style={{
            width: '100%', background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`,
            borderRadius: 8, padding: '10px 14px', color: C.text, fontFamily: F.display,
            fontSize: 13, outline: 'none',
          }}
        />
        <div style={{ fontSize: 10, color: C.dim, marginTop: 4 }}>
          Distinct count drives "Implied Team Size" on the investor dashboard.
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={onSave} disabled={loading} style={btn(C.neon)}>
          {loading ? 'SAVING…' : 'SAVE DEFAULTS'}
        </button>
        <button onClick={onPreview} disabled={loading} style={btn(C.cyan)}>
          PREVIEW APPLY-TO-ORPHANS
        </button>
        {preview && (
          <button onClick={onApply} disabled={loading} style={btn(C.amber)}>
            APPLY ({preview.workItemsTouched} items)
          </button>
        )}
        {statusMsg && (
          <span style={{ fontSize: 11, color: C.neon, fontFamily: F.mono }}>{statusMsg}</span>
        )}
      </div>

      {preview && (
        <div style={{
          marginTop: 12, padding: 10, background: `${C.cyan}10`,
          border: `1px solid ${C.cyan}30`, borderRadius: 6,
          fontSize: 11, fontFamily: F.mono, color: C.cyan,
        }}>
          DRY-RUN: scanned {preview.scanned} work items. {preview.workItemsTouched} would
          be touched, filling {preview.totalFieldsFilled} NULL fields
          {preview.compositesUpdated > 0 && ` (+ ${preview.compositesUpdated} composite scores recomputed)`}.
          Click APPLY to commit.
        </div>
      )}
    </GlassPanel>
  );
}

// ─── Slider ─────────────────────────────────────────────────────────────────

function DimensionSlider({
  label, hint, max, value, onChange,
}: {
  label: string;
  hint: string;
  max: number;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const v = value ?? 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: C.text, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 13, fontFamily: F.mono, color: value === null ? C.dim : C.violet }}>
          {value === null ? '—' : value}
        </span>
      </div>
      <input
        type="range" min={0} max={max} step={1}
        value={v}
        onChange={e => onChange(Number(e.target.value) === 0 ? null : Number(e.target.value))}
        style={{ width: '100%' }}
      />
      <div style={{ fontSize: 10, color: C.dim, lineHeight: 1.4, marginTop: 2 }}>{hint}</div>
    </div>
  );
}

const btn = (color: string): React.CSSProperties => ({
  background: `${color}15`, border: `1px solid ${color}40`, color,
  fontFamily: F.mono, fontSize: 11, fontWeight: 600, padding: '8px 16px',
  borderRadius: 6, cursor: 'pointer', letterSpacing: '0.06em',
});
